import React, { useState, useRef } from 'react';
import { X, Upload, FileText, Download, AlertTriangle, CheckCircle2, ArrowLeft, Database, Archive } from 'lucide-react';
import { importAllData, RestoreResult } from '../../services/comprehensiveBackupService';
import { useToast } from '../../contexts/ToastContext';

interface DataRestoreModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDataRestored: () => void;
}


export function DataRestoreModal({ isOpen, onClose, onDataRestored }: DataRestoreModalProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [restoreResults, setRestoreResults] = useState<RestoreResult | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showSuccess, showError } = useToast();

  if (!isOpen) return null;

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!file.name.toLowerCase().endsWith('.zip')) {
      showError('Please upload a ZIP backup file');
      return;
    }

    // Show confirmation dialog before processing
    setPendingFile(file);
    setShowConfirmation(true);
  };

  const confirmRestore = async () => {
    if (!pendingFile) return;

    setIsProcessing(true);
    setRestoreResults(null);

    try {
      const results = await importAllData(pendingFile);
      setRestoreResults(results);
      setShowConfirmation(false);
      setPendingFile(null);

      if (results.success && results.errors.length === 0) {
        showSuccess(`Data restored successfully! ${results.tablesRestored} tables, ${results.recordsRestored} records.`);
        onDataRestored();
      } else if (results.success) {
        showError(`Data restored with ${results.errors.length} warnings`);
      } else {
        showError('Data restoration failed');
      }
    } catch (error) {
      showError('Failed to restore data');
      console.error('Data restoration error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl border border-white/20 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <button
            onClick={onClose}
            className="inline-flex items-center px-3 py-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-all duration-200 group mr-4"
          >
            <ArrowLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform duration-200" />
            <span className="text-sm font-medium">Back</span>
          </button>
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center">
              <Database className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-slate-800">Import/Restore Data from Backup</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-xl transition-colors duration-200"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Warning Notice */}
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-medium text-red-900 mb-2">⚠️ Data Overwrite Warning</h3>
                <p className="text-sm text-red-700 mb-3">
                  This operation will completely replace your current data with the backup data. 
                  All existing clients, reminders, and email templates will be overwritten.
                </p>
                <p className="text-sm text-red-700 font-medium">
                  Make sure to export your current data before proceeding if you want to keep it.
                </p>
              </div>
            </div>
          </div>

          {/* File Upload Area */}
          <div className="flex items-center justify-center w-full">
            <label 
              className={`flex flex-col items-center justify-center w-full h-64 border-2 border-dashed rounded-2xl cursor-pointer transition-all duration-300 ${
                dragActive 
                  ? 'border-purple-400 bg-purple-50/50 scale-105' 
                  : isProcessing 
                    ? 'border-slate-300 bg-slate-50/50' 
                    : 'border-slate-300 bg-slate-50/50 hover:border-purple-400 hover:bg-purple-50/50'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                {isProcessing ? (
                  <>
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mb-4"></div>
                    <p className="text-lg font-medium text-slate-700 mb-2">Processing backup file...</p>
                    <p className="text-sm text-slate-500">Please wait while we analyze your data</p>
                  </>
                ) : (
                  <>
                    <Upload className={`w-12 h-12 mb-4 transition-colors duration-300 ${
                      dragActive ? 'text-purple-500' : 'text-slate-400'
                    }`} />
                    <p className="text-lg font-medium text-slate-700 mb-2">
                      <span className="font-semibold">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-sm text-slate-500 mb-4">Complete system backup ZIP files</p>
                    <div className="flex items-center gap-2 text-xs text-slate-400">
                      <FileText className="w-4 h-4" />
                      <span>Supported: .zip files only</span>
                    </div>
                  </>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".zip"
                onChange={handleFileChange}
                disabled={isProcessing}
              />
            </label>
          </div>

          {/* Supported File Formats */}
          <div className="bg-slate-50 rounded-xl p-4">
            <h3 className="font-medium text-slate-800 mb-3">Comprehensive Backup System</h3>
            <div className="flex items-start space-x-3">
              <Database className="w-5 h-5 text-slate-500 mt-0.5" />
              <div>
                <h4 className="font-medium text-slate-700">Complete System Backup</h4>
                <p className="text-sm text-slate-600">
                  This tool restores complete system backups including: customers, contacts, locations, bookings, instructors,
                  quotes, invoices, certificates, reminders, email templates, admin tasks, and all other system data.
                </p>
              </div>
            </div>
          </div>

          {/* Restore Results */}
          {restoreResults && (
            <div className="space-y-4">
              {/* Success Results */}
              {restoreResults.success && restoreResults.recordsRestored > 0 && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="font-medium text-emerald-900 mb-2">Data Successfully Restored</h3>
                      <div className="text-sm text-emerald-800">
                        <div className="mb-2">
                          ✓ {restoreResults.tablesRestored} table{restoreResults.tablesRestored !== 1 ? 's' : ''} restored
                        </div>
                        <div>
                          ✓ {restoreResults.recordsRestored} record{restoreResults.recordsRestored !== 1 ? 's' : ''} imported
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Error Results */}
              {restoreResults.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 max-h-48 overflow-y-auto">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="font-medium text-red-900 mb-2">
                        {restoreResults.errors.length} error{restoreResults.errors.length !== 1 ? 's' : ''} occurred
                      </h3>
                      <div className="space-y-1 text-sm text-red-800">
                        {restoreResults.errors.map((error, index) => (
                          <p key={index}>{error}</p>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end p-6 border-t border-slate-200">
          <button
            onClick={onClose}
            className="px-6 py-3 border border-slate-300 text-slate-700 font-medium rounded-xl hover:bg-slate-50 transition-colors duration-200"
          >
            Close
          </button>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl border border-white/20 w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-800">Confirm Data Restoration</h3>
              </div>
              
              <p className="text-slate-600 mb-6">
                This will permanently replace all your current data with the backup data. 
                This action cannot be undone. Are you sure you want to proceed?
              </p>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowConfirmation(false);
                    setPendingFile(null);
                  }}
                  className="flex-1 px-4 py-3 border border-slate-300 text-slate-700 font-medium rounded-xl hover:bg-slate-50 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmRestore}
                  disabled={isProcessing}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white font-medium rounded-xl hover:shadow-lg transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isProcessing ? 'Restoring...' : 'Confirm Restore'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}